import React from "react";
import {
  Divider,
  Checkbox,
  Typography,
  Box,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { CoachBriefInfoDto } from "@growth-machine-llc/stridist-api-client";
import FiltersOnlyThisButton from "../exercise-library/buttons/FiltersOnlyThisButton";
import { ALL_OPTION } from "../filters/filterUtils";
import { exerciseTagCategoryMultiSelectStyles } from "../exercise-library/exerciseLibraryStyles";

const useStyles = makeStyles((theme) => ({
  checkbox: {
    padding: 0,
    marginRight: theme.spacing(1),
  },

  onlyThisButton: {
    height: 22,
  },

  coachOption: {
    alignItems: "center",
    "&:hover $onlyThisButton": {
      opacity: 1,
    },
  },
}));

interface CoachFilterContentProps {
  selectedCoachIds: number[];
  onCoachesChange: (ids?: number[], allOptionsSelected?: boolean) => void;
  coaches?: CoachBriefInfoDto[];
  allIsSelected?: boolean;
  onAllToggle?: () => void;
}

export function CoachFilterContent({
  selectedCoachIds = [],
  onCoachesChange,
  coaches = [],
  allIsSelected = false,
  onAllToggle,
}: CoachFilterContentProps) {
  const s = useStyles();
  const classes = exerciseTagCategoryMultiSelectStyles();

  const coachIds = React.useMemo(
    () => coaches.map((coach) => coach.id),
    [coaches],
  );

  const isSelected = (id: number) => selectedCoachIds.includes(id);

  const toggleCoach = (coachId: number) => {
    const isAlreadySelected = selectedCoachIds.includes(coachId);
    const newSelected = isAlreadySelected
      ? selectedCoachIds.filter((id) => id !== coachId)
      : [...selectedCoachIds, coachId];
    if (newSelected.length === 0) {
      return;
    } else {
      onCoachesChange(newSelected);
    }
  };

  const handleOnlyThisChange = (coachId: number) => {
    onCoachesChange(coachIds.filter((id) => id === coachId));
  };

  const handleAllToggle = React.useCallback(() => {
    if (onAllToggle) {
      onAllToggle();
    }
  }, [onAllToggle]);

  return (
    <>
      <Box
        className={clsx(classes.tagOption, s.coachOption)}
        key={ALL_OPTION}
        display="flex"
        alignItems="center"
        width="100%"
        onClick={handleAllToggle}
      >
        <Checkbox
          className={s.checkbox}
          color="primary"
          checked={allIsSelected}
          onChange={handleAllToggle}
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent={"space-between"}
          width="100%"
        >
          <Typography sx={{ fontWeight: 500 }}>All</Typography>
        </Box>
      </Box>

      <Divider />

      {coaches.map((coach) => (
        <React.Fragment key={coach.id}>
          <Box
            className={clsx(classes.tagOption, s.coachOption)}
            onClick={() => toggleCoach(coach.id)}
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                className={s.checkbox}
                color="primary"
                checked={isSelected(coach.id)}
                onChange={() => toggleCoach(coach.id)}
              />
            </Box>
            <Box display={"flex"} flexDirection="column" width="100%">
              <Box
                display="flex"
                alignItems="center"
                justifyContent={"space-between"}
              >
                <ListItemAvatar>
                  <Avatar src={coach.photoUrl}>
                    {(coach.displayName || "?").charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={coach.displayName}
                  secondary={coach.email}
                />
                <FiltersOnlyThisButton
                  handleOnlyThisChange={() => handleOnlyThisChange(coach.id)}
                  className={s.onlyThisButton}
                />
              </Box>
            </Box>
          </Box>
          <Divider />
        </React.Fragment>
      ))}
    </>
  );
}
